import React, { useState, useMemo } from "react";
import { MdClose, MdCalendarMonth, MdWork, MdOutlineNotifications, MdWarningAmber } from "react-icons/md";
import { CommonImages } from "../../../../reusableUI/images";
import moment from "moment";

const PartnerNotification = ({
  isOpen,
  onClose,
  notifications = [],
  onNotificationClick,
  onMarkAllAsRead,
  onViewAllClick,
  loading = false,
}) => {
  const [filter, setFilter] = useState("All");
  const [showConfirmMarkAll, setShowConfirmMarkAll] = useState(false);

  const handleMarkAllConfirm = () => {
    if (onMarkAllAsRead) {
      onMarkAllAsRead();
    }
    setShowConfirmMarkAll(false);
  };

  const handleViewAll = () => {
    onClose();
    if (onViewAllClick) {
      onViewAllClick();
    }
  };

  const filteredNotifications = useMemo(() => {
    return notifications
      .filter((notif) => {
        if (filter === "Unread") {
          return (
            String(notif?.status || "").toLowerCase() === "unseen" ||
            String(notif?.status || "").toLowerCase() === "unread"
          );
        }
        if (filter === "Read") {
          return (
            String(notif?.status || "").toLowerCase() === "seen" ||
            String(notif?.status || "").toLowerCase() === "read"
          );
        }
        return true;
      })
      .sort(
        (a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf(),
      );
  }, [notifications, filter]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-end z-[60]">
      <div className="w-full md:w-[450px] h-full bg-[#f8f9fa] shadow-2xl flex flex-col transform transition-transform duration-300 translate-x-0">
        {/* Header */}
        <div className="flex flex-col p-4 bg-white border-b border-gray-200 shadow-sm z-10">
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-row items-center gap-1 text-blue-600">
              <MdOutlineNotifications size={24} />
              <h2 className="text-[16px] font-semibold font-poppins  whitespace-nowrap">
                Notifications
              </h2>
            </div>

            <div className="flex items-center gap-3 ml-auto">
              {(() => {
                const hasUnread = notifications.some(
                  (notif) =>
                    String(notif?.status || "").toLowerCase() === "unseen" ||
                    String(notif?.status || "").toLowerCase() === "unread"
                );
                return (
                  <button
                    onClick={() => setShowConfirmMarkAll(true)}
                    disabled={!hasUnread}
                    className={`text-[11px] font-medium font-poppins transition-colors mr-2 ${
                      hasUnread
                        ? "text-blue-600 hover:text-blue-800 hover:underline"
                        : "text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    Mark all as read
                  </button>
                );
              })()}

              {/* Close Button */}
              <button
                onClick={onClose}
                className="p-1.5 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
              >
                <MdClose size={22} className="text-gray-600" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-4 mt-4">
            {["All", "Unread", "Read"].map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`text-[13px] font-medium font-poppins pb-1.5 border-b-2 transition-colors outline-none ${
                  filter === tab
                    ? "border-[#3E57E8] text-[#3E57E8]"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto pt-2">
          {loading && notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 p-6 text-center">
              <span className="inline-block w-6 h-6 border-2 border-[#3E57E8] border-t-transparent rounded-full animate-spin mb-3"></span>
              <p className="font-poppins text-[13px] font-medium text-gray-700">
                Loading notifications...
              </p>
            </div>
          ) : filteredNotifications.length > 0 ? (
            <div className="flex flex-col pb-4">
              {filteredNotifications.map((notif) => {
                const isUnseen =
                  String(notif?.status || "").toLowerCase() === "unseen" ||
                  String(notif?.status || "").toLowerCase() === "unread";

                return (
                  <div
                    key={notif._id || notif.id}
                    onClick={() =>
                      onNotificationClick && onNotificationClick(notif)
                    }
                    className={`p-4 mx-4 mb-3 rounded-xl border transition-all duration-300 ${
                      isUnseen
                        ? "bg-[#F8FAFF] border-[#3E57E8] shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } cursor-pointer hover:shadow-md hover:-translate-y-0.5`}
                  >
                    <div className="flex gap-3">
                      {/* Notification Icon based on type */}
                      <div
                        className={`mt-1 h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isUnseen
                            ? "bg-blue-100 text-blue-600"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {(() => {
                          const type = String(notif?.type || "").trim();
                          const title = String(notif?.title || "").toLowerCase();
                          
                          if (type === "Visit") {
                            if (
                              title.includes("duty ended") ||
                              title.includes("duty started") ||
                              title.includes("standby specialist assigned")
                            ) {
                              return <MdWork size={16} />;
                            }
                            return <MdCalendarMonth size={16} />;
                          }
                          return <div className="h-2 w-2 rounded-full bg-current"></div>;
                        })()}
                      </div>

                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-1 gap-2">
                          <h3
                            className={`text-[13px] font-semibold font-poppins ${
                              isUnseen ? "text-[#1D1D22]" : "text-gray-700"
                            }`}
                          >
                            {notif.title}
                          </h3>
                          <span className="text-[11px] text-gray-500 font-poppins whitespace-nowrap mt-0.5">
                            {moment(notif.createdAt).fromNow()}
                          </span>
                        </div>
                        <p className="text-[12px] text-gray-600 font-poppins leading-relaxed">
                          {notif.message}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 p-6 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <MdOutlineNotifications size={32} className="text-gray-300" />
              </div>
              <p className="font-poppins text-[14px] font-medium text-gray-700">
                No notifications found
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 bg-white border-t border-gray-200 text-center shadow-[0_-2px_10px_rgba(0,0,0,0.02)]">
          <button
            onClick={handleViewAll}
            className="text-[13px] text-blue-600 font-medium font-poppins hover:underline"
          >
            View All
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmMarkAll && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mb-4">
                <MdWarningAmber size={28} />
              </div>
              <h3 className="text-[16px] font-bold text-gray-800 font-poppins mb-2">Mark All as Read</h3>
              <p className="text-[13px] text-gray-600 font-poppins mb-6">
                Are you sure you want to mark all your notifications as read?
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowConfirmMarkAll(false)}
                  className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium font-poppins text-[13px] hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMarkAllConfirm}
                  className="flex-1 py-2.5 rounded-lg bg-blue-600 text-white font-medium font-poppins text-[13px] hover:bg-blue-700 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerNotification;
